import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { api } from './api';

export type Role =
  | 'PLATFORM_OWNER'
  | 'PLATFORM_SUPPORT'
  | 'WORKSPACE_ADMIN'
  | 'OPERADOR'
  | 'CONTADOR_EXTERNO';

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  workspaceId: string | null;
  effectiveWorkspaceId: string | null;
  actingAs: 'self' | 'impersonation';
  workspaceName: string | null;
}

type LoginResult = { ok: true } | { ok: false; requiresMfa: true; mfaChallenge: string };

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  loginMfa: (mfaChallenge: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  isAuthenticated: boolean;
  isPlatform: boolean;
  isImpersonating: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

function isValidUser(x: unknown): x is User {
  if (!x || typeof x !== 'object') return false;
  const u = x as Partial<User>;
  return typeof u.id === 'string' && typeof u.email === 'string' && typeof u.role === 'string';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Geração monotônica — só commita resultado de refresh se ainda for a versão "atual".
  // Login/logout bumpam essa geração pra invalidar refresh em voo (fix race condition
  // onde o /me inicial, lento por cold-start, sobrescrevia o resultado do login).
  const genRef = useRef(0);

  async function refresh() {
    const myGen = ++genRef.current;
    try {
      const me = await api.get<User>('/v1/auth/me');
      if (genRef.current !== myGen) return;
      setUser(isValidUser(me) ? me : null);
    } catch {
      if (genRef.current !== myGen) return;
      setUser(null);
    } finally {
      if (genRef.current === myGen) setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login(email: string, password: string): Promise<LoginResult> {
    // Invalida qualquer refresh em voo — o resultado dele não pode mais sobrescrever.
    genRef.current++;
    const r = await api.post<User | { requiresMfa: true; mfaChallenge: string }>(
      '/v1/auth/login',
      { email, password },
    );
    if (r && typeof r === 'object' && 'requiresMfa' in r && r.requiresMfa) {
      return { ok: false, requiresMfa: true, mfaChallenge: r.mfaChallenge };
    }
    if (!isValidUser(r)) throw new Error('Resposta inválida do servidor');
    // flushSync força o React a commitar user/loading no DOM ANTES de retornar.
    // Sem isso, o navigate('/') do onSubmit pode montar o AppLayout lendo o
    // estado antigo (user=null) e disparar window.location.href='/login' —
    // exatamente o sintoma "tela pisca e volta pra login na 1a tentativa".
    flushSync(() => {
      setUser(r);
      setLoading(false);
    });
    return { ok: true };
  }

  async function loginMfa(mfaChallenge: string, code: string) {
    genRef.current++;
    const u = await api.post<User>('/v1/auth/login/mfa', { mfaChallenge, code });
    if (!isValidUser(u)) throw new Error('Resposta inválida do servidor');
    flushSync(() => {
      setUser(u);
      setLoading(false);
    });
  }

  async function logout() {
    genRef.current++;
    await api.post('/v1/auth/logout').catch(() => {});
    setUser(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginMfa,
        logout,
        refresh,
        isAuthenticated: !!user,
        isPlatform: user?.role === 'PLATFORM_OWNER' || user?.role === 'PLATFORM_SUPPORT',
        isImpersonating: user?.actingAs === 'impersonation',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
